import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { CheckCircle, Zap } from 'lucide-react'
import { signupEnergyOsUser } from '../api/auth'
import { ENERGY_BRAIN_BUSINESS_ID } from '../api/client'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'

// The localStorage key EnergyOSHomeownerDashboard.tsx reads on first login to auto-create the
// home without asking the user to re-enter country/MPID — the signup form is the only place
// that ever collects them, since /auth/signup itself only creates the auth-service user account
// (the Home is created later, once logged in, via POST /energy-os/me/home).
export const ENERGY_OS_SIGNUP_HOME_KEY = 'energyOsSignupHome'

export function EnergyOSSignup() {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    country: '', mpid: '', firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '',
  })
  const [error,   setError]   = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return }
    if (!ENERGY_BRAIN_BUSINESS_ID) { setError('EnergyOS signup is not configured — contact support.'); return }

    setLoading(true)
    try {
      await signupEnergyOsUser({
        businessId: ENERGY_BRAIN_BUSINESS_ID,
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        password: form.password,
      })
      localStorage.setItem(ENERGY_OS_SIGNUP_HOME_KEY, JSON.stringify({ country: form.country, mpid: form.mpid }))
      setSuccess(true)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg ?? 'Signup failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={22} className="text-emerald-600" />
          </div>
          <h2 className="text-lg font-semibold text-ink mb-2">Check your email</h2>
          <p className="text-sm text-ink-dim mb-6">
            We sent a verification link to <strong className="text-ink">{form.email}</strong>.
            Click it, then log in to finish setting up your home.
          </p>
          <Button variant="primary" onClick={() => navigate('/login')}>
            Back to sign in
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[380px]">

        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
            <Zap size={16} className="text-white" />
          </div>
          <span className="text-ink font-semibold text-lg tracking-tight">EnergyOS</span>
        </div>

        <div className="bg-white rounded-xl border border-paper-border p-7">
          <h1 className="text-lg font-semibold text-ink mb-0.5">Create your account</h1>
          <p className="text-sm text-ink-dim mb-6">Connect your home to EnergyOS</p>

          {error && (
            <div className="mb-5 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Country">
                <input required value={form.country} onChange={set('country')}
                  placeholder="FR" className="field" />
              </Field>
              <Field label="MPID" description="Your meter's identifier">
                <input required value={form.mpid} onChange={set('mpid')}
                  placeholder="123456789012" className="field" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <input required value={form.firstName} onChange={set('firstName')}
                  placeholder="Jane" className="field" />
              </Field>
              <Field label="Last name">
                <input required value={form.lastName} onChange={set('lastName')}
                  placeholder="Doe" className="field" />
              </Field>
            </div>
            <Field label="Email">
              <input type="email" required value={form.email} onChange={set('email')}
                placeholder="you@example.com" className="field" />
            </Field>
            <Field label="Phone">
              <input type="tel" required value={form.phone} onChange={set('phone')}
                placeholder="+1 555 000 0000" className="field" />
            </Field>
            <Field label="Password">
              <input type="password" required value={form.password} onChange={set('password')}
                placeholder="••••••••" className="field" />
            </Field>
            <Field label="Confirm password">
              <input type="password" required value={form.confirmPassword} onChange={set('confirmPassword')}
                placeholder="••••••••" className="field" />
            </Field>

            <Button type="submit" variant="primary" loading={loading} className="w-full justify-center py-2 mt-1">
              {loading ? 'Creating…' : 'Create account'}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-ink-dim mt-5">
          Already have an account?{' '}
          <Link to="/login" className="text-accent font-medium hover:text-accent-hover transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
